import dayjs from "dayjs";

export type StatusKey =
  | "semStatus"
  | "pendente"
  | "emAndamento"
  | "feito"
  | "aguardandoRevisao"
  | "aguardandoRetorno"
  | "validacaoIPV7";

export type ChipKey =
  | "pending"
  | "inProgress"
  | "completed"
  | "waitingReview"
  | "waitingReturn"
  | "arquive";

export interface StatusMeta {
  key: StatusKey;
  label: string;
  chipKey: ChipKey;
}

export const STATUS_LIST: StatusMeta[] = [
  { key: "semStatus", label: "Sem status", chipKey: "arquive" },
  { key: "pendente", label: "Pendente", chipKey: "pending" },
  { key: "emAndamento", label: "Em andamento", chipKey: "inProgress" },
  { key: "feito", label: "Concluída", chipKey: "completed" },
  {
    key: "aguardandoRevisao",
    label: "Aguardando revisão",
    chipKey: "waitingReview",
  },
  {
    key: "aguardandoRetorno",
    label: "Aguardando retorno",
    chipKey: "waitingReturn",
  },
  { key: "validacaoIPV7", label: "Validação IPV7", chipKey: "arquive" },
];

export const DONE_STATUS_KEY: StatusKey = "feito";

export const STATUS_BY_KEY = STATUS_LIST.reduce(
  (acc, meta) => {
    acc[meta.key] = meta;
    return acc;
  },
  {} as Record<StatusKey, StatusMeta>,
);

export const STATUS_ABERTAS: StatusMeta[] = STATUS_LIST.filter(
  (meta) => meta.key !== DONE_STATUS_KEY,
);

export type TipoAnexo = "imagem" | "arquivo";

export interface Anexo {
  id: string;
  url: string;
  nome?: string;
  mimeType?: string;
  tamanho?: number;
  tipo: TipoAnexo;
}

export interface AnexoNovo {
  nome: string;
  url: string;
  mimeType: string;
  tamanho: number;
  tipo: TipoAnexo;
}

export interface Comentario {
  id: string;
  autor: string;
  autorId: string;
  autorFoto?: string;
  externo?: boolean;
  texto: string;
  anexos: Anexo[];
  criadoEm: string;
  respostas: Comentario[];
}

export interface Categoria {
  id: number;
  nome: string;
  subcategorias: { id: number; nome: string }[];
}

export interface Abertura {
  data: string;
  criadoPor: string;
  observacao: string;
  anexos: Anexo[];
  categorias: Categoria[];
}

export interface Conclusao {
  data: string;
  fechadoPor: string;
  observacaoProblema: string;
  observacaoSolucao: string;
  categorias: Categoria[];
}

export interface Demanda {
  id: string;
  codigo: string;
  titulo: string;
  responsavel: string;
  responsavelFoto?: string;
  status: StatusKey;
  dataPrevista: string;
  dataCriacao: string;
  concluida: boolean;
  arquivada?: boolean;
  abertura: Abertura | null;
  conclusao: Conclusao | null;
  comentarios: Comentario[];
  detalheCarregado?: boolean;
  pendenteSync?: boolean;
  descartado?: boolean;
  tentativas?: number;
  erroSync?: string | null;
}

export interface ComposerPayload {
  texto: string;
  imagens: AnexoNovo[];
  anexosMantidos: string[];
}

export type AbaChamados = "abertas" | "concluidas" | "arquivadas";

export type SortField =
  | "codigo"
  | "titulo"
  | "responsavel"
  | "status"
  | "dataPrevista"
  | "dataCriacao";

export type SortDirection = "asc" | "desc";

export interface Periodo {
  inicio: string | null;
  fim: string | null;
}

const AVATAR_COLORS = [
  "blue",
  "green",
  "purple",
  "orange",
  "pink",
  "red",
  "yellow",
] as const;

export type CromaticKey = (typeof AVATAR_COLORS)[number];

export function nameToColorKey(name: string): CromaticKey {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function nomeAbreviado(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return parts[0] ?? "";
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

export function formatarData(iso?: string | null): string {
  if (!iso) return "—";
  const data = dayjs(iso);
  return data.isValid() ? data.format("DD/MM/YYYY") : "—";
}

export function formatarDataHora(iso?: string | null): string {
  if (!iso) return "—";
  const data = dayjs(iso);
  return data.isValid() ? data.format("DD/MM/YYYY [às] HH:mm") : "—";
}

export function isVencida(demanda: Demanda): boolean {
  if (demanda.concluida || !demanda.dataPrevista) return false;
  const prevista = dayjs(demanda.dataPrevista);
  return prevista.isValid() && prevista.isBefore(dayjs().startOf("day"));
}

export function tipoDoMime(mimeType?: string): TipoAnexo {
  return String(mimeType ?? "").startsWith("image/") ? "imagem" : "arquivo";
}

export function formatarTamanho(bytes?: number): string {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function contarComentarios(demanda: Demanda): number {
  return demanda.comentarios.reduce(
    (total, comentario) => total + 1 + comentario.respostas.length,
    0,
  );
}
